import { FilterControllerContext, FilterControllerT } from '../filter-controller';
import { FilterData, FilterTag } from '../filter-data';

import * as Utils from '../../utils';

/**
 * Defines application-provided parameters for a text filter.
 */
export interface TextFilterParams {
  /**
   * A list of recent searches.
   */
  recents?: string[];

  /**
   * Max number of recent searches.  If 0, no recents are saved.
   */
  maxRecentCount?: number;

  /**
   * Delimiter for multi-value filtering.
   */
  delimiter?: string;

  /**
   * Placeholder text for search input (default: 'Search <column header>').
   */
  searchHint?: string;

  /**
   * To specific max input string length
   */
  maxLength?: number | undefined;

  /**
   * Method that will evaluate the value in real-time and determine if it's valid or not.
   */
  validator?: (value: string) => boolean | string;
}

/**
 * Defines user-configured data for a text filter.
 */
export class TextFilterData implements FilterData {
  /**
   * User-provided search text.
   */
  value?: string;

  /** FilterData implementation */

  clear(): void {
    this.value = undefined;
  }

  copyFrom(data: TextFilterData): void {
    this.value = data?.value;
  }

  hasFilter(): boolean {
    return !!this.value; // All falsy strings are invalid.
  }

  getTags(filterParams: TextFilterParams): FilterTag[] {
    if (this.hasFilter()) {
      if (filterParams?.delimiter) {
        // Create a set as the user may have typed the same string twice, like "hello,hello".
        return Utils.makeDistinct(this.value.split(filterParams.delimiter)).map(value => {
          return {
            description: value,
            remove: () => {
              if (this.hasFilter()) {
                const newFilterData = new TextFilterData();
                newFilterData.copyFrom(this);
                // newFilterData.value = undefined;
                newFilterData.value = newFilterData.value
                  .split(filterParams.delimiter)
                  .filter(value_ => value_ !== value)
                  .join(filterParams.delimiter);
                return newFilterData;
              } else {
                return this;
              }
            }
          };
        });
      } else {
        return [
          {
            description: this.value,
            remove: () => {
              if (this.hasFilter()) {
                const newFilterData = new TextFilterData();
                newFilterData.copyFrom(this);
                newFilterData.value = undefined;
                return newFilterData;
              } else {
                return this;
              }
            }
          }
        ];
      }
    } else {
      return [];
    }
  }
}

/**
 * Defines the filter handler for a text filter.
 * The HTML template for the text filter is defined in virtual-grid-header-dropdown.html.
 */
export class TextFilterController extends FilterControllerT<TextFilterData, TextFilterParams> {
  constructor(context: FilterControllerContext) {
    super(context, () => {
      return new TextFilterData();
    });

    this.initialFocusElementName = 'search-input';

    this.errors = {};
    this._resetErrors();
  }

  getTemplateContext(): any {
    const context = super.getTemplateContext();

    context.errors = this.errors;

    context.onModelChange = () => {
      if (this.sourceParams.validator && this.localData.value) {
        const result: boolean | string = this.sourceParams.validator(this.localData.value);
        if (typeof result == 'string') {
          this.errors.value = !!result;
          this.errors.message = result || undefined;
        } else {
          this.errors.value = result !== true;
          this.errors.message = undefined;
        }
      } else {
        this._resetErrors();
      }
    };

    context.removeSearch = (): void => {
      this.localData.value = undefined;
      this._resetErrors();
      this.focusInitialElement();
    };

    context.removeRecent = (recent: string): void => {
      const index: number = this.sourceParams.recents.indexOf(recent);
      if (index !== -1) {
        this.sourceParams.recents.splice(index, 1);
      }
    };

    return context;
  }

  onClear(): void {
    super.onClear();
    this._resetErrors();
  }

  canApply(): boolean {
    return !this.errors.value;
  }

  protected evaluateLocalData(): boolean {
    const hasChanged = this.localData.value !== this.sourceData?.value;

    // If the local has changed from the source, then update the recents collection before returning.
    if (this.canClear()) {
      // We ensure there are params in FilterControllerT.init.
      Utils.assert(!!this.sourceParams, 'There should be at least an empty params object at this point.');
      // Ensure there is a recent array.
      this.sourceParams.recents = this.sourceParams.recents || [];

      // Set up some locals for cleaner code below.
      const recent: string[] = this.sourceParams.recents;
      const maxCount: number = this.sourceParams.maxRecentCount;

      // See if the value is in the recent list.
      const index: number = recent.indexOf(this.localData.value);

      // If the item is already at the front, there is nothing to do.
      if (index !== 0) {
        // Manipulate the most recent list.
        if (index !== -1) {
          // If the value is in the recent list, move it to the front (most recently used).
          recent.unshift(...recent.splice(index, 1));
        } else if (maxCount === undefined || maxCount > 0) {
          // If no max count was specified in the params, or a valid one was, add the value to the front of the list.
          recent.unshift(this.localData.value);
          // Check the new length.
          if (recent.length > maxCount) {
            // If the new length exceeds the max, then remove the oldest value.
            recent.splice(maxCount, recent.length - maxCount);
          }
        }
      }
    }

    return hasChanged;
  }

  protected onInit(): void {
    super.onInit();
    this._resetErrors();
  }

  private _resetErrors(): void {
    this.errors.value = false;
    this.errors.message = undefined;
  }

  private errors: {
    value?: boolean;
    message?: string;
  };
}
